import { toast as toastNative } from 'react-toastify'

import {
  createAction as createActionNative,
  createAsyncThunk as createAsyncThunkNative,
  createSelector,
} from '@reduxjs/toolkit'
import type { AsyncThunkOptions, AsyncThunkPayloadCreator, PayloadAction, PayloadActionCreator } from '@reduxjs/toolkit'
import { cloneDeep, uniqBy } from 'lodash'
import { capitalizeFirstLetter, deleteLastPoint } from 'packages/helper'
import type { ThunkMiddleware } from 'redux-thunk'

import { actionCaching } from './actionCaching'
import { SliceAction } from './createBuilder'
import {
  CreateAsyncSliceReadonlyParams,
  CreatorAsyncSliceOptions,
  CustomInitialState,
  DefaultData,
  DefaultList,
  DefaultMeta,
  isList,
  RejectError,
  RejectValuePayload,
} from './types'
import type { AsyncThunk, CreateAsyncSliceParams, RejectValue } from './types'

export interface ReduxConfigurationOptions {
  createAction?: typeof createActionNative
  createAsyncThunk?: typeof createAsyncThunkNative
  toast?: typeof toastNative
  deleteLastPoint?: boolean
}

export class ReduxConfiguration {
  private readonly _createAction = createActionNative
  private readonly _createAsyncThunk = createAsyncThunkNative
  private readonly _toast = toastNative
  private readonly _deleteLastPoint: boolean = false

  constructor({
    createAsyncThunk,
    createAction,
    toast,
    deleteLastPoint: isDeleteLastPoint,
  }: ReduxConfigurationOptions) {
    if (createAction) {
      this._createAction = createAction
    }
    if (createAsyncThunk) {
      this._createAsyncThunk = createAsyncThunk
    }
    if (toast) {
      this._toast = toast
    }
    this._deleteLastPoint = !!isDeleteLastPoint
  }

  createAction = <P = void, T extends string = string>(type: T): PayloadActionCreator<P, T> =>
    this._createAction<P, T>(type)

  createAsyncThunk = <Returned, ThunkArg = void>(
    typePrefix: string,
    payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, RejectValue>,
    options?: AsyncThunkOptions<ThunkArg, RejectValue> & Pick<CreateAsyncSliceParams<Returned, ThunkArg>, 'error'>,
  ) => {
    const { error: errorFn, ...actionOptions } = options ?? {}
    const payloadCreatorWithError = (
      ...args: Parameters<AsyncThunkPayloadCreator<Returned, ThunkArg, RejectValue>>
    ) => {
      const result = payloadCreator(...args) as Promise<any>
      if (typeof result.catch === 'undefined') {
        return result
      }
      return result.catch((apiError) => {
        const error = typeof errorFn === 'function' ? errorFn() : errorFn
        return args[1].rejectWithValue({
          message:
            typeof error === 'object' && !error?.message && apiError?.message
              ? apiError?.message
              : typeof error === 'string'
              ? error
              : error?.message,
          showToast: typeof error === 'string' ? true : error?.showToast === true,
        })
      })
    }

    return this._createAsyncThunk<Returned, ThunkArg, RejectValuePayload>(
      typePrefix,
      errorFn ? payloadCreatorWithError : payloadCreator,
      actionOptions,
    ) as AsyncThunk<Returned, ThunkArg, RejectValuePayload>
  }

  rejectedToastifyMiddleware: ThunkMiddleware =
    () => (next) => (action: PayloadAction<RejectError> & { error?: any; meta?: any }) => {
      if (action.type.endsWith('rejected')) {
        const error =
          typeof action.payload === 'string'
            ? { message: action.payload }
            : action.payload?.message || typeof action.payload?.showToast !== 'undefined'
            ? action.payload
            : typeof action.error === 'string'
            ? { message: action.error }
            : action.error ?? null

        const errorMessage = this._deleteLastPoint ? deleteLastPoint(error.message) : error.message

        if (errorMessage && error?.showToast !== false) {
          this._toast.error(errorMessage)
        }

        return next({
          type: action.type,
          meta: action.meta,
          payload: errorMessage,
        })
      }

      return next(action)
    }

  getCreatorAsyncSlice = <RootState>(options: CreatorAsyncSliceOptions<RootState>) => {
    const createAsyncSlice = <
      Returned,
      ThunkArg = void,
      NameAction extends string = string,
      NameSlice extends string = string,
      AddToInitialState extends Record<string, any> | undefined = undefined,
    >(
      params: CreateAsyncSliceParams<Returned, ThunkArg, AddToInitialState> &
        CreateAsyncSliceReadonlyParams<NameAction, NameSlice>,
    ) => {
      const isCashing = params.isCashing ?? options.isCashing
      const cash = params.cash ?? options.cash

      const action = this.createAsyncThunk<Returned, ThunkArg>(
        `${String(options.nameReducer)}/${params.nameAction}`,
        actionCaching<Returned, ThunkArg>(params.promise, isCashing, cash),
        {
          ...params.options,
          error: params.error,
        },
      )

      const clearAction = this.createAction(
        `${String(options.nameReducer)}/clear${capitalizeFirstLetter(params.nameSlice)}`,
      )

      const initialState = {
        loading: false,
        ready: false,
        error: null,
        data: null,
        ...(typeof params.addToInitialState === 'function' ? params.addToInitialState() : params.addToInitialState),
      }

      const initialSlice = {
        [params.nameSlice]: initialState,
      } as { [K in NameSlice]: CustomInitialState<Returned, AddToInitialState> }

      const selectState = (state: RootState) => state[options.nameReducer]
      const selector = createSelector(
        selectState,
        (state) => state?.[params.nameSlice as unknown as keyof typeof state],
      ) as unknown as (state: RootState) => CustomInitialState<Returned, AddToInitialState>

      let prevState: DefaultList<DefaultData<Returned> | null, DefaultMeta<Returned> | null | undefined> = {
        data: null,
        meta: undefined,
      }

      const sliceCases = [
        {
          action: action.pending,
          reducer: <State extends typeof initialSlice>(state: State) => {
            state[params.nameSlice].loading = true
            state[params.nameSlice].error = null
            if (params.optimisticReducer) {
              prevState = cloneDeep({
                data: state[params.nameSlice].data,
                meta: state[params.nameSlice].meta,
              })
              const optimisticData = params.optimisticReducer(
                state[params.nameSlice].meta
                  ? ({
                      data: state[params.nameSlice].data,
                      meta: state[params.nameSlice].meta,
                    } as Returned)
                  : state[params.nameSlice].data,
              )
              state[params.nameSlice].data = isList(optimisticData) ? optimisticData.data : optimisticData
              if (isList(optimisticData)) {
                state[params.nameSlice].meta = optimisticData.meta
              } else {
                delete state[params.nameSlice].meta
              }
            }
            if (params.onPending) {
              params.onPending(state[params.nameSlice])
            }
          },
        },
        {
          action: action.rejected,
          reducer: <State extends typeof initialSlice>(state: State, { payload }: PayloadAction<string>) => {
            state[params.nameSlice].loading = false
            state[params.nameSlice].ready = true
            state[params.nameSlice].error = payload
            if (params.optimisticReducer) {
              const optimisticData = params.optimisticReducer(
                prevState.meta
                  ? ({
                      data: prevState.data,
                      meta: prevState.meta,
                    } as Returned)
                  : prevState.data,
              )
              state[params.nameSlice].data = isList(optimisticData) ? optimisticData.data : optimisticData
              if (isList(optimisticData)) {
                state[params.nameSlice].meta = optimisticData.meta
              } else {
                delete state[params.nameSlice].meta
              }
            }
            if (params.onRejected) {
              params.onRejected(state[params.nameSlice], payload)
            }
          },
        },
        {
          action: action.fulfilled,
          reducer: <State extends typeof initialSlice>(state: State, { payload }: PayloadAction<Returned>) => {
            state[params.nameSlice].loading = false
            state[params.nameSlice].error = null
            state[params.nameSlice].ready = true

            state[params.nameSlice].data = isList(payload)
              ? params.isAccumulate && Array.isArray(payload.data)
                ? uniqBy(
                    [...((state[params.nameSlice].data ?? []) as any[]), ...payload.data],
                    params.accumulateUniqBy ?? 'id',
                  )
                : payload.data
              : params.isAccumulate && Array.isArray(payload)
              ? uniqBy(
                  [...((state[params.nameSlice].data ?? []) as any[]), ...payload],
                  params.accumulateUniqBy ?? 'id',
                )
              : payload
            if (isList(payload)) {
              state[params.nameSlice].meta = payload.meta
            } else {
              delete state[params.nameSlice].meta
            }

            if (params.onFulfilled) {
              params.onFulfilled(state[params.nameSlice], payload)
            }
          },
        },
        {
          action: clearAction,
          reducer: <State extends typeof initialSlice>(state: State) => {
            state[params.nameSlice] = initialState
          },
        },
      ] as SliceAction<typeof initialSlice>[]

      const actionObject = {
        [params.nameAction]: action,
      } as { [K in NameAction]: typeof action }

      const clearActionObject = {
        [`clear${capitalizeFirstLetter(params.nameSlice)}`]: clearAction,
      } as { [K in `clear${Capitalize<NameSlice>}`]: typeof clearAction }

      const sliceObject = {
        [`initialSlice${capitalizeFirstLetter(params.nameSlice)}`]: initialSlice,
      } as { [K in `initialSlice${Capitalize<NameSlice>}`]: typeof initialSlice }

      const selectorObject = {
        [`select${capitalizeFirstLetter(params.nameSlice)}`]: selector,
      } as { [K in `select${Capitalize<NameSlice>}`]: typeof selector }

      const sliceCasesObject = {
        [`sliceCases${capitalizeFirstLetter(params.nameSlice)}`]: sliceCases,
      } as { [K in `sliceCases${Capitalize<NameSlice>}`]: typeof sliceCases }

      return {
        ...actionObject,
        ...clearActionObject,
        ...sliceObject,
        ...selectorObject,
        ...sliceCasesObject,
      }
    }

    return { createAsyncSlice }
  }
}
